import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUpcomingAdjustments } from '../../../Redux/Actions/actions';
import { FaChartLine, FaCalendarAlt, FaHome, FaUser } from 'react-icons/fa';
import style from './UpcomingAdjustments.module.css';

const UpcomingAdjustments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { upcomingAdjustments, rentalsLoading, rentalsError } = useSelector(state => state);

  const { daysWindow } = useMemo(() => {
    return { daysWindow: 30 };
  }, []);

  useEffect(() => {
    dispatch(fetchUpcomingAdjustments(daysWindow));
  }, [dispatch, daysWindow]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return 'Sin fecha';
    return parsed.toLocaleDateString('es-AR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getAdjustmentDate = (item) => {
    return item?.nextAdjustmentDate || item?.appliedFrom || item?.rental?.nextAdjustmentDate || item?.rental?.nextAdjustment || null;
  };

  const getRental = (item) => item?.rental || item;

  const adjustmentsThisWindow = (upcomingAdjustments || []).filter(item => {
    const dateString = getAdjustmentDate(item);
    if (!dateString) return false;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return false;
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= daysWindow;
  });

  const getDaysToAdjustment = (dateString) => {
    if (!dateString) return null;
    const target = new Date(dateString);
    if (Number.isNaN(target.getTime())) return null;
    const now = new Date();
    const diffTime = target - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (rentalsLoading) {
    return <div className={style.loading}>Cargando ajustes próximos...</div>;
  }

  if (rentalsError) {
    return <div className={style.error}>Error: {rentalsError}</div>;
  }

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaChartLine className={style.titleIcon} />
          Ajustes Programados (próximos 30 días)
        </h1>
        <div className={style.subtitle}>
          Se muestran contratos con ajuste de cuota dentro de los próximos 30 días.
        </div>
      </div>

      <div className={style.summary}>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Total Ajustes (30 días)</span>
          <span className={style.summaryValue}>{adjustmentsThisWindow.length}</span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Ventana de días</span>
          <span className={style.summaryValue}>{daysWindow}</span>
        </div>
      </div>

      {adjustmentsThisWindow.length === 0 ? (
        <div className={style.noData}>
          <FaChartLine className={style.noDataIcon} />
          <h3>No hay ajustes programados en los próximos 30 días</h3>
          <p>Probá revisar más adelante.</p>
        </div>
      ) : (
        <div className={style.list}>
          {adjustmentsThisWindow
            .sort((a, b) => {
              const dateA = new Date(getAdjustmentDate(a) || 0);
              const dateB = new Date(getAdjustmentDate(b) || 0);
              return dateA - dateB;
            })
            .map((item) => {
              const rental = getRental(item);
              const property = rental?.property || item?.property;
              const adjustmentDate = getAdjustmentDate(item);
              const daysToAdjustment = getDaysToAdjustment(adjustmentDate);
              const mainTenant = rental?.tenants?.find(t => t.tenantType === 'main');

              return (
                <div key={item.id || rental?.id} className={style.card}>
                  <div className={style.cardHeader}>
                    <div className={style.propertyInfo}>
                      <FaHome className={style.propertyIcon} />
                      <div>
                        <h3 className={style.propertyTitle}>
                          {property?.title || 'Propiedad sin título'}
                        </h3>
                        <p className={style.propertyAddress}>
                          {property?.street} {property?.number}, {property?.locality}
                        </p>
                      </div>
                    </div>
                    <div className={style.badge}>
                      <FaCalendarAlt />
                      {formatDate(adjustmentDate)}
                      {Number.isFinite(daysToAdjustment) && (
                        <span className={style.badgeSubtext}>
                          {daysToAdjustment >= 0 ? `En ${daysToAdjustment} días` : 'Vencido'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={style.cardContent}>
                    <div className={style.infoGrid}>
                      <div className={style.infoItem}>
                        <FaUser className={style.infoIcon} />
                        <div>
                          <span className={style.infoLabel}>Inquilino Principal</span>
                          <span className={style.infoValue}>
                            {mainTenant?.fullName || 'No asignado'}
                          </span>
                        </div>
                      </div>

                      <div className={style.infoItem}>
                        <span className={style.infoLabel}>Tipo de Ajuste</span>
                        <span className={style.infoValue}>
                          {(rental?.adjustmentType || item?.adjustmentType || 'ICL').toUpperCase()}
                        </span>
                      </div>

                      <div className={style.infoItem}>
                        <span className={style.infoLabel}>Frecuencia</span>
                        <span className={style.infoValue}>
                          Cada {rental?.adjustmentFrequency || item?.adjustmentFrequency || 12} meses
                        </span>
                      </div>

                      <div className={style.infoItem}>
                        <span className={style.infoLabel}>Alquiler Actual</span>
                        <span className={style.infoValue}>
                          {formatCurrency(rental?.currentRentValue || rental?.rentValue || item?.currentRentValue)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={style.cardActions}>
                    {/* <button
                      className={`${style.actionButton} ${style.primary}`}
                      onClick={() => navigate(`/alquileres/contrato/${rental?.id}`)}
                    >
                      Ver Contrato
                    </button> */}
                    <button
                      className={`${style.actionButton} ${style.secondary}`}
                      onClick={() => navigate(`/alquileres/contrato/${rental?.id}/pagos`)}
                    >
                      Gestionar Pagos
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default UpcomingAdjustments;
